import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ItemSelector } from '@/components/widget/item-selector';
import { useAnchorProposals, useApplyDriveRoots, useContentList, useDecideAnchorProposals, useReopenAnchorProposal } from '@/hooks';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import type { DriveAnchorDecisionValue, DriveAnchorProposalSafe, DriveRootSafe } from '@vivport/backend/types';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

type ProjectsPanelProps = {
  root: DriveRootSafe;
  driveId: number;
  driveName: string;
};

type Choice = { decision: DriveAnchorDecisionValue; anchorId: number | null };

const DECISION_LABELS: Record<DriveAnchorDecisionValue, string> = {
  LINK: 'Link existing project',
  CREATE: 'Create project',
  REJECT: 'Skip',
};

function defaultChoice(proposal: DriveAnchorProposalSafe): Choice {
  return proposal.proposal === 'LINK_EXISTING'
    ? { decision: 'LINK', anchorId: proposal.matchedAnchorId }
    : { decision: 'CREATE', anchorId: null };
}

function capitalise(value: string): string {
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function buildDecisionSummary(
  proposals: readonly DriveAnchorProposalSafe[],
  choices: Record<number, Choice>,
  anchorTitles: Map<number, string>
): string {
  const creates: DriveAnchorProposalSafe[] = [];
  const links: { path: string; text: string }[] = [];
  let skips = 0;
  for (const proposal of proposals) {
    const choice = choices[proposal.id] ?? defaultChoice(proposal);
    if (choice.decision === 'CREATE') {
      creates.push(proposal);
    } else if (choice.decision === 'LINK') {
      const target = choice.anchorId === null ? '(no project chosen)' : (anchorTitles.get(choice.anchorId) ?? `Project ${choice.anchorId}`);
      links.push({ path: proposal.path, text: `${proposal.folderName} → ${target}` });
    } else {
      skips += 1;
    }
  }
  creates.sort((a, b) => a.path.localeCompare(b.path));
  links.sort((a, b) => a.path.localeCompare(b.path));
  const clauses: string[] = [];
  if (creates.length > 0) {
    clauses.push(`create ${creates.length} project${creates.length === 1 ? '' : 's'}: ${creates.map((c) => c.folderName).join(', ')}`);
  }
  if (links.length > 0) {
    clauses.push(`link ${links.length} folder${links.length === 1 ? '' : 's'} to existing projects: ${links.map((l) => l.text).join(', ')}`);
  }
  if (skips > 0) {
    clauses.push(`skip ${skips}`);
  }
  return capitalise(clauses.join('; '));
}

export function ProjectsPanel({ root, driveId, driveName }: ProjectsPanelProps) {
  const proposalsQuery = useAnchorProposals(root.id, true);
  const anchorsQuery = useContentList({ contentTypes: ['ANCHOR'], limit: 100 });
  const decide = useDecideAnchorProposals();
  const reopen = useReopenAnchorProposal();
  const applyRoots = useApplyDriveRoots();
  const [choices, setChoices] = useState<Record<number, Choice>>({});
  const [decidedOpen, setDecidedOpen] = useState(false);

  const proposals = proposalsQuery.data?.proposals ?? [];
  const pending = proposals.filter((proposal) => proposal.status === 'PENDING');
  const decided = proposals.filter((proposal) => proposal.status !== 'PENDING');

  const loadedProposals = proposalsQuery.data?.proposals;
  useEffect(() => {
    setChoices((prev) => {
      const next: Record<number, Choice> = {};
      for (const proposal of loadedProposals ?? []) {
        if (proposal.status === 'PENDING') {
          next[proposal.id] = prev[proposal.id] ?? defaultChoice(proposal);
        }
      }
      return next;
    });
  }, [loadedProposals]);

  const anchorItems = (anchorsQuery.data?.items ?? []).map((item) => ({
    id: item.id,
    title: 'title' in item && typeof item.title === 'string' ? item.title : null,
  }));
  const anchorTitles = new Map<number, string>();
  for (const item of anchorItems) {
    anchorTitles.set(item.id, item.title ?? `Project ${item.id}`);
  }
  for (const proposal of proposals) {
    if (proposal.matchedAnchorId !== null && proposal.matchedAnchorTitle !== null) {
      anchorTitles.set(proposal.matchedAnchorId, proposal.matchedAnchorTitle);
    }
  }

  const matchedCount = pending.filter((proposal) => (choices[proposal.id] ?? defaultChoice(proposal)).decision === 'LINK').length;
  const createCount = pending.filter((proposal) => (choices[proposal.id] ?? defaultChoice(proposal)).decision === 'CREATE').length;
  const linkMissingAnchor = pending.some((proposal) => {
    const choice = choices[proposal.id] ?? defaultChoice(proposal);
    return choice.decision === 'LINK' && choice.anchorId === null;
  });

  const handleApprove = () => {
    const summary = buildDecisionSummary(pending, choices, anchorTitles);
    if (!window.confirm(`${summary}. Continue?`)) {
      return;
    }
    decide.mutate(
      {
        decisions: pending.map((proposal) => {
          const choice = choices[proposal.id] ?? defaultChoice(proposal);
          return {
            proposalId: proposal.id,
            decision: choice.decision,
            ...(choice.decision === 'LINK' && choice.anchorId !== null ? { anchorId: choice.anchorId } : {}),
          };
        }),
      },
      {
        onSuccess: (result) => toast.success(`Approved. Filed ${result.filed} file${result.filed === 1 ? '' : 's'}.`),
        onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to decide proposals.')),
      }
    );
  };

  const handleApplyRoots = () => {
    applyRoots.mutate(driveId, {
      onSuccess: (result) =>
        toast.success(`Filed ${result.filed}, retagged ${result.retagged}, awaiting approval ${result.awaitingApproval}.`),
      onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to apply roots.')),
    });
  };

  const DecidedChevron = decidedOpen ? ChevronUp : ChevronDown;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium">Projects · {root.path}</p>
          <p className="text-xs text-muted-foreground">
            {pending.length === 0
              ? 'No folders awaiting approval.'
              : `${pending.length} folder${pending.length === 1 ? '' : 's'}: ${matchedCount} match existing projects, ${createCount} new`}
          </p>
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={handleApplyRoots} disabled={applyRoots.isPending}>
            {applyRoots.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Apply roots now
          </Button>
          {pending.length > 0 ? (
            <Button type="button" size="sm" onClick={handleApprove} disabled={decide.isPending || linkMissingAnchor}>
              {decide.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Approve
            </Button>
          ) : null}
        </div>
      </div>

      {proposalsQuery.isLoading ? <p className="text-xs text-muted-foreground">Loading proposals...</p> : null}
      {proposalsQuery.isError ? (
        <p className="text-xs text-destructive">{getAxiosErrorDisplayMessage(proposalsQuery.error, 'Failed to load proposals.')}</p>
      ) : null}

      {pending.length > 0 ? (
        <ul className="space-y-2">
          {pending.map((proposal) => {
            const choice = choices[proposal.id] ?? defaultChoice(proposal);
            return (
              <li key={proposal.id} className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-center">
                <span className="font-mono text-xs">{proposal.path}</span>
                <Select
                  value={choice.decision}
                  onValueChange={(value) =>
                    setChoices((prev) => ({
                      ...prev,
                      [proposal.id]: { decision: value as DriveAnchorDecisionValue, anchorId: choice.anchorId ?? proposal.matchedAnchorId },
                    }))
                  }
                >
                  <SelectTrigger className="w-[190px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LINK">{DECISION_LABELS.LINK}</SelectItem>
                    <SelectItem value="CREATE">{DECISION_LABELS.CREATE}</SelectItem>
                    <SelectItem value="REJECT">{DECISION_LABELS.REJECT}</SelectItem>
                  </SelectContent>
                </Select>
                {choice.decision === 'LINK' ? (
                  <div className="w-[240px]">
                    <ItemSelector
                      entityName="project"
                      displayName="Project"
                      items={anchorItems}
                      selectedId={choice.anchorId}
                      onSelect={(id) => setChoices((prev) => ({ ...prev, [proposal.id]: { decision: 'LINK', anchorId: id } }))}
                      loading={anchorsQuery.isLoading}
                    />
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {choice.decision === 'CREATE' ? `New project "${proposal.folderName}" in ${driveName}` : 'Folder stays unmapped'}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}

      {decided.length > 0 ? (
        <div>
          <button
            type="button"
            onClick={() => setDecidedOpen((prev) => !prev)}
            aria-expanded={decidedOpen}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <DecidedChevron className="h-3 w-3" />
            Recently decided ({decided.length})
          </button>
          {decidedOpen ? (
            <ul className="mt-1 space-y-1">
              {decided.map((proposal) => (
                <li key={proposal.id} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono">{proposal.path}</span>
                  <Badge variant={proposal.status === 'REJECTED' ? 'outline' : 'secondary'}>{proposal.status.toLowerCase()}</Badge>
                  {proposal.matchedAnchorTitle ? <span className="text-muted-foreground">→ {proposal.matchedAnchorTitle}</span> : null}
                  {proposal.status === 'REJECTED' ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={reopen.isPending}
                      onClick={() =>
                        reopen.mutate(proposal.id, {
                          onError: (error: unknown) => toast.error(getAxiosErrorDisplayMessage(error, 'Failed to reopen.')),
                        })
                      }
                    >
                      Reopen
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
